/**
 * SSE(Server-Sent Events) 라인 파서 — 결정 #14.
 *
 * 프로바이더의 스트리밍 응답은 `data: {...}\n\n` 형태의 SSE다. ReadableStream을
 * UTF-8로 디코딩하며 줄 단위로 잘라 `data:` 페이로드만 뽑아낸다. 멀티바이트
 * 문자가 청크 경계에 걸쳐도 깨지지 않도록 TextDecoder({stream:true})로 누적한다.
 *
 * iOS 15 Safari의 fetch 스트리밍 버퍼링 위험(결정 #14)은 이 파서가 아니라
 * 논스트리밍 폴백 경로(gateway에서 선택)로 대응한다. 여기서는 표준 SSE만 다룬다.
 */

/** ReadableStream을 SSE `data:` 페이로드 문자열의 async iterable로 변환. */
export async function* parseSSE(
  stream: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // 이벤트는 빈 줄로 구분되지만, 대부분의 LLM API는 줄 단위 data:로 보낸다.
      let nlIndex: number;
      while ((nlIndex = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, nlIndex).trim();
        buffer = buffer.slice(nlIndex + 1);
        if (line === "" || line.startsWith(":")) continue; // 빈 줄/주석 무시
        if (line.startsWith("data:")) {
          const payload = line.slice(5).trim();
          if (payload) yield payload;
        }
      }
    }
    // 남은 버퍼 처리(마지막 줄에 개행이 없는 경우).
    const tail = buffer.trim();
    if (tail.startsWith("data:")) {
      const payload = tail.slice(5).trim();
      if (payload) yield payload;
    }
  } finally {
    reader.releaseLock();
  }
}
